/**
 * How someone has arranged the session pages on this device: whether the contents are kept open beside the page,
 * and how wide the panels are. It is kept in the browser, not with the user, because the arrangement that suits a
 * desktop does not suit the same person's tablet.
 *
 * Storage can be missing or refuse, in a private window for one, and the pages must work the same without it.
 */
const STORAGE_KEY = 'somat.session.layout'

type SessionLayout = {
    pinnedNav?: boolean
    panelSizes?: Record<string, number>
    dismissedLargerDeviceNotice?: boolean
}

const read = (): SessionLayout => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null')
        return stored && typeof stored === 'object' ? stored : {}
    } catch {
        return {}
    }
}

/**
 * @returns What was kept under the key, or undefined where nothing was or it cannot be read.
 */
export function readSessionLayout<K extends keyof SessionLayout>(key: K): SessionLayout[K] {
    return read()[key]
}

export function storeSessionLayout<K extends keyof SessionLayout>(key: K, value: SessionLayout[K]): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({...read(), [key]: value}))
    } catch {
        // Not kept; the arrangement lasts until the page is left.
    }
}
